import React, { useEffect, useState } from 'react';
import { API, authHeaders } from '../utils/helpers';
import CountPill from './CountPill';

// ── A ticket's quotes (24 Oct) ───────────────────────────────────────────────
//
// In TicketDetail, under the line items: every quote that belongs to this
// ticket (extra work quoted on it, and the new-job quote it was written from),
// with what happened to each. Spinboard's own Postgres, never Lightspeed, so
// it costs nothing and never holds the ticket up. Shows nothing when the
// ticket has no quotes, or the person cannot see Quotes.

const LOOK = {
  draft: ['Draft', '#8a9ab8'], sent: ['Sent', '#3498db'], approved: ['Approved', '#2ecc71'],
  declined: ['Not now', '#e67e22'], expired: ['Expired', '#e74c3c'], replaced: ['Replaced', '#8a9ab8'],
};
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const day = (iso) => { try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); } catch (_) { return ''; } };

// Pure: what a ticket's lines are, to tell whether they changed since an
// estimate was made. The SAME rule as server/helpers/quotes.js
// ticketFingerprint (check-estimates.js holds them together).
export function ticketFingerprint(lineItems) {
  return (Array.isArray(lineItems) ? lineItems : []).filter(l => !l.isTimerLine)
    .map(l => `${l.isInventory ? 'i' : 'l'}:${l.id}:${Number(l.quantity) || 0}:${(Number(l.unitPrice) || 0).toFixed(2)}:${l.discountID || ''}:${l.warranty ? 1 : 0}`)
    .sort().join('|');
}

export default function TicketQuotes({ ticketId, T, onOpenQuote, lineItems = null, refreshKey = 0, onSendEstimate = null }) {
  const [quotes, setQuotes] = useState([]);
  useEffect(() => {
    let live = true;
    setQuotes([]);
    fetch(`${API}/api/quotes/for-ticket/${encodeURIComponent(ticketId)}`, { credentials: 'include', headers: authHeaders() })
      .then(r => (r.ok ? r.json() : { quotes: [] }))
      .then(d => { if (live) setQuotes(Array.isArray(d.quotes) ? d.quotes : []); })
      .catch(() => {});
    return () => { live = false; };
  }, [ticketId, refreshKey]);

  // The ticket's latest estimate still out with the customer (or "not now"),
  // when the ticket has changed since: the customer is looking at old numbers.
  const lastEstimate = [...quotes].reverse().find(q => q.kind === 'estimate' && q.displayStatus !== 'draft');
  const stale = lastEstimate && lineItems && lastEstimate.fingerprint && !lastEstimate.replaced
    && ['sent', 'declined', 'expired'].includes(lastEstimate.displayStatus)
    && lastEstimate.fingerprint !== ticketFingerprint(lineItems);

  if (!quotes.length) return null;
  return (
    <div style={{ marginTop: 14, border: `1px solid ${T.cardBorder || T.border}`, borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: T.textSub, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
        Quotes and estimates on this ticket <CountPill n={quotes.length} T={T} size={11} style={{ marginLeft: 4 }} />
      </div>
      {stale && (
        <div style={{ fontSize: 12.5, color: T.text, background: 'rgba(230,126,34,0.12)', border: '1px solid rgba(230,126,34,0.45)', borderRadius: 8, padding: '8px 10px', margin: '4px 0 8px', lineHeight: 1.5 }}>
          <strong>The ticket has changed since {lastEstimate.label} was sent.</strong> The customer is looking at the old estimate.
          {onSendEstimate && <button onClick={onSendEstimate} style={{ marginLeft: 8, background: 'none', border: 'none', color: '#e67e22', fontWeight: 700, cursor: 'pointer', padding: 0, fontFamily: 'inherit', fontSize: 12.5, textDecoration: 'underline' }}>Send a new estimate</button>}
        </div>
      )}
      {quotes.map(q => {
        const [label, colour] = LOOK[q.displayStatus] || [q.displayStatus, '#888'];
        const what = q.displayStatus === 'approved'
          ? `Approved ${day(q.decidedAt)}${q.decidedBy ? ` by ${q.decidedBy} for the customer` : ' by the customer'}`
          : q.displayStatus === 'declined'
            ? `Not now ${day(q.decidedAt)}${q.decisionReason ? `: ${q.decisionReason}` : ''}`
            : q.sentAt ? `Sent ${day(q.sentAt)}` : `Started ${day(q.createdAt)}${q.createdBy ? ` by ${q.createdBy}` : ''}`;
        return (
          <button key={q.id} onClick={() => onOpenQuote(q.id)}
            style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 10, padding: '8px 0', background: 'none', border: 'none',
              borderTop: `1px solid ${T.border}`, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', color: T.text,
              opacity: q.displayStatus === 'replaced' ? 0.6 : 1 }}>
            <span style={{ fontWeight: 700, fontSize: 13, minWidth: 72 }}>{q.label}</span>
            <span style={{ fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 10, color: colour, border: `1px solid ${colour}66`, whiteSpace: 'nowrap' }}>{label}</span>
            <span style={{ flex: 1, fontSize: 12, color: T.textSub, minWidth: 0 }}>
              {q.kind === 'extra' ? 'Extra work' : q.kind === 'estimate' ? 'Estimate' : 'New job'} · {what}
              {q.estimateStatusNote ? ` · ${q.estimateStatusNote}` : ''}
              {q.decisionNote ? <span style={{ fontStyle: 'italic' }}> · “{q.decisionNote}”</span> : null}
            </span>
            <span style={{ fontWeight: 700, fontSize: 13 }}>{money(q.total)}</span>
          </button>
        );
      })}
    </div>
  );
}
